import * as THREE from 'three';
import { CSS3DObject } from 'three/examples/jsm/renderers/CSS3DRenderer.js';
import { FikoSahne } from './FikoSahne';

/**
 * Professional CAD-style CSS3D Annotation System
 *
 * Features:
 * - HTML-based annotations rendered in 3D space via CSS3DRenderer
 * - Professional CATIA/SolidWorks-like appearance
 * - Title + Description layout with clean borders
 * - Multiple leader lines with labeled connection points (A, B, C, etc.)
 * - Automatic anchor point calculation
 * - Fixed size option (zoom-independent)
 * - Full customization support
 *
 * Based on Three.js CSS3DRenderer for crisp, resolution-independent text
 */

/**
 * Connection point configuration for leader lines
 */
export interface FikoConnectionPoint {
    /** 3D world position where the leader line ends */
    position: THREE.Vector3;
    /** Label for the connection point (A, B, C, 1, 2, 3, etc.) */
    label: string;
    /** Optional color for this specific connection */
    color?: string;
}

/**
 * Annotation style configuration
 */
export interface FikoAnnotationStyle {
    /** Main title text */
    baslik?: string;
    /** Description text (can be multiline) */
    aciklama?: string;
    /** Background color */
    arkaplanRengi?: string;
    /** Border color */
    cercevRengi?: string;
    /** Border width in pixels */
    cercevKalinlik?: number;
    /** Text color */
    metinRengi?: string;
    /** Title font size */
    baslikBoyut?: number;
    /** Description font size */
    aciklamaBoyut?: number;
    /** Leader line color */
    cizgiRengi?: string;
    /** Leader line width */
    cizgiKalinlik?: number;
    /** Connection point circle radius */
    noktaYaricap?: number;
    /** Fixed size regardless of zoom */
    sabitBoyut?: boolean;
    /** Font family */
    fontAilesi?: string;
    /** Padding inside the annotation box */
    padding?: number;
    /** Maximum width of annotation box */
    maxGenislik?: number;
}

const DEFAULT_STYLE: Required<FikoAnnotationStyle> = {
    baslik: '',
    aciklama: '',
    arkaplanRengi: 'rgba(255, 255, 255, 0.95)',
    cercevRengi: '#333333',
    cercevKalinlik: 1,
    metinRengi: '#1a1a1a',
    baslikBoyut: 14,
    aciklamaBoyut: 12,
    cizgiRengi: '#333333',
    cizgiKalinlik: 1.5,
    noktaYaricap: 8,
    sabitBoyut: true,
    fontAilesi: 'Arial, Helvetica, sans-serif',
    padding: 12,
    maxGenislik: 280
};

/**
 * FikoCSS3DAnnotation - Professional 3D annotation with HTML rendering
 */
export class FikoCSS3DAnnotation extends THREE.Group {
    override type = 'FikoCSS3DAnnotation';

    // Style configuration
    private style: Required<FikoAnnotationStyle>;

    // Connection points for leader lines
    private connectionPoints: FikoConnectionPoint[] = [];

    // CSS3D Objects
    private css3dObject: CSS3DObject | null = null;
    private htmlElement: HTMLDivElement | null = null;

    // Leader lines (Three.js lines connecting to points)
    private leaderLines: THREE.Line[] = [];
    private connectionCircles: THREE.Mesh[] = [];
    private connectionLabels: CSS3DObject[] = [];

    // Reference to scenes
    public sahne: FikoSahne | null = null;
    public css3dSahne: THREE.Scene | null = null;

    // Sabit boyut support
    private sabitBoyut: boolean = true;
    private hazirlaListenerEklendi: boolean = false;
    private kontrolDegistiEvent = () => this.kameraYonDegisti();
    private originalScale: THREE.Vector3 = new THREE.Vector3(1, 1, 1);

    // Anchor position (where the annotation box is placed)
    private anchorPosition: THREE.Vector3 = new THREE.Vector3();

    // Pre-allocated vectors for performance
    private static readonly tempVec3 = new THREE.Vector3();

    constructor(
        sahne?: FikoSahne,
        css3dSahne?: THREE.Scene,
        styleConfig?: FikoAnnotationStyle
    ) {
        super();

        this.sahne = sahne || null;
        this.css3dSahne = css3dSahne || null;

        // Merge style with defaults
        this.style = { ...DEFAULT_STYLE, ...styleConfig };
        this.sabitBoyut = this.style.sabitBoyut;

        this.name = 'CSS3D Annotation';
        this.renderOrder = 1000;

        // Initialize if sahne provided
        if (sahne) {
            this.hazirla(sahne);
        }
    }

    /**
     * Initialize annotation with HTML element
     */
    init(): void {
        this.createHTMLElement();
        this.updateCSS3DObject();
    }

    /**
     * Create the HTML element for annotation
     */
    private createHTMLElement(): void {
        // Main container
        this.htmlElement = document.createElement('div');
        this.htmlElement.className = 'fiko-css3d-annotation';

        // CRITICAL: CSS3DRenderer needs elements with explicit dimensions
        // Apply styles with explicit width/height to ensure visibility
        Object.assign(this.htmlElement.style, {
            backgroundColor: this.style.arkaplanRengi,
            border: `${this.style.cercevKalinlik}px solid ${this.style.cercevRengi}`,
            borderRadius: '4px',
            padding: `${this.style.padding}px`,
            maxWidth: `${this.style.maxGenislik}px`,
            minWidth: '150px',
            fontFamily: this.style.fontAilesi,
            color: this.style.metinRengi,
            boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
            pointerEvents: 'none',
            userSelect: 'none',
            whiteSpace: 'pre-wrap',
            lineHeight: '1.4',
            // CRITICAL for CSS3D visibility
            position: 'absolute',
            display: 'block',
            visibility: 'visible',
            opacity: '1',
            zIndex: '1000',
            // Transform properties for 3D
            transformStyle: 'preserve-3d',
            backfaceVisibility: 'visible',
            willChange: 'transform'
        });

        // Title element
        if (this.style.baslik) {
            const titleEl = document.createElement('div');
            titleEl.className = 'annotation-title';
            titleEl.textContent = this.style.baslik;
            Object.assign(titleEl.style, {
                fontSize: `${this.style.baslikBoyut}px`,
                fontWeight: 'bold',
                marginBottom: this.style.aciklama ? '6px' : '0',
                color: this.style.metinRengi,
                textShadow: '0 1px 2px rgba(0,0,0,0.1)'
            });
            this.htmlElement.appendChild(titleEl);
        }

        // Description element
        if (this.style.aciklama) {
            const descEl = document.createElement('div');
            descEl.className = 'annotation-description';
            descEl.textContent = this.style.aciklama;
            Object.assign(descEl.style, {
                fontSize: `${this.style.aciklamaBoyut}px`,
                color: this.style.metinRengi,
                opacity: '0.85'
            });
            this.htmlElement.appendChild(descEl);
        }

    }

    /**
     * Create or update CSS3D object
     */
    private updateCSS3DObject(): void {
        if (!this.htmlElement) {
            return;
        }

        // Remove old CSS3D object from scene
        if (this.css3dObject && this.css3dSahne) {
            this.css3dSahne.remove(this.css3dObject);
        }

        // CRITICAL: Ensure HTML element has proper styling for visibility
        this.htmlElement.style.position = 'absolute';
        this.htmlElement.style.visibility = 'visible';
        this.htmlElement.style.opacity = '1';
        this.htmlElement.style.display = 'block';
        this.htmlElement.style.transformStyle = 'preserve-3d';
        this.htmlElement.style.backfaceVisibility = 'visible';

        // Create new CSS3D object
        this.css3dObject = new CSS3DObject(this.htmlElement);
        this.css3dObject.position.copy(this.position);

        // Scale down for 3D scene (HTML is in pixels, 3D is in world units)
        // IMPORTANT: Scale needs to be appropriate for the scene's unit system
        // For CAD scenes in mm, 0.01 means 1 pixel = 0.01 units = 0.01mm
        // Try larger scale to ensure visibility
        const scale = 0.02;  // Increased from 0.005 for better visibility
        this.css3dObject.scale.set(scale, scale, scale);
        this.originalScale.set(scale, scale, scale);

        // Add to CSS3D scene - CRITICAL: must add to css3dSahne for CSS3DRenderer to render it
        if (this.css3dSahne) {
            this.css3dSahne.add(this.css3dObject);
        }
    }

    /**
     * Set annotation position
     */
    setPosition(pos: THREE.Vector3): void {
        this.position.copy(pos);
        this.anchorPosition.copy(pos);

        if (this.css3dObject) {
            this.css3dObject.position.copy(pos);
        }

        // Update leader lines
        this.updateLeaderLines();
    }

    /**
     * Set CSS3D scene reference (call after init if css3dSahne was not available)
     */
    setCSS3DSahne(css3dSahne: THREE.Scene): void {
        this.css3dSahne = css3dSahne;

        // Re-add CSS3DObject if it exists
        if (this.css3dObject) {
            this.css3dSahne.add(this.css3dObject);
        }
    }

    /**
     * Add a connection point with leader line
     */
    addConnectionPoint(point: FikoConnectionPoint): void {
        this.connectionPoints.push({
            position: point.position.clone(),
            label: point.label,
            color: point.color || this.style.cizgiRengi
        });

        this.createLeaderLine(this.connectionPoints.length - 1);
    }

    /**
     * Set multiple connection points
     */
    setConnectionPoints(points: FikoConnectionPoint[]): void {
        // Clear existing
        this.clearLeaderLines();

        // Add new points
        this.connectionPoints = points.map(p => ({
            position: p.position.clone(),
            label: p.label,
            color: p.color || this.style.cizgiRengi
        }));

        // Create leader lines for all points
        this.connectionPoints.forEach((_, index) => {
            this.createLeaderLine(index);
        });
    }

    /**
     * Create a leader line to a connection point
     */
    private createLeaderLine(index: number): void {
        const point = this.connectionPoints[index];
        if (!point) return;

        // Create line geometry
        const lineGeometry = new THREE.BufferGeometry();
        const positions = new Float32Array([
            this.position.x, this.position.y, this.position.z,
            point.position.x, point.position.y, point.position.z
        ]);
        lineGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

        // Create line material
        const lineMaterial = new THREE.LineBasicMaterial({
            color: new THREE.Color(point.color || this.style.cizgiRengi),
            linewidth: this.style.cizgiKalinlik,
            transparent: true,
            opacity: 0.9,
            depthTest: false,
            depthWrite: false
        });

        const line = new THREE.Line(lineGeometry, lineMaterial);
        line.renderOrder = 999;
        this.leaderLines.push(line);

        // Add line to main scene
        if (this.sahne) {
            this.sahne.add(line);
        }

        // Create connection point circle
        this.createConnectionCircle(index);

        // Create connection point label
        this.createConnectionLabel(index);
    }

    /**
     * Create circle marker at connection point
     */
    private createConnectionCircle(index: number): void {
        const point = this.connectionPoints[index];
        if (!point) return;

        // Create circle geometry
        const circleGeometry = new THREE.CircleGeometry(this.style.noktaYaricap * 0.01, 32);

        // Create material with border effect
        const circleMaterial = new THREE.MeshBasicMaterial({
            color: new THREE.Color(this.style.arkaplanRengi),
            transparent: true,
            opacity: 0.95,
            side: THREE.DoubleSide,
            depthTest: false,
            depthWrite: false
        });

        const circle = new THREE.Mesh(circleGeometry, circleMaterial);
        circle.position.copy(point.position);
        circle.renderOrder = 1001;

        // Make circle face camera (billboard)
        circle.userData.isBillboard = true;

        this.connectionCircles.push(circle);

        // Add to scene
        if (this.sahne) {
            this.sahne.add(circle);
        }

        // Create border ring
        const ringGeometry = new THREE.RingGeometry(
            this.style.noktaYaricap * 0.008,
            this.style.noktaYaricap * 0.01,
            32
        );
        const ringMaterial = new THREE.MeshBasicMaterial({
            color: new THREE.Color(point.color || this.style.cercevRengi),
            transparent: true,
            opacity: 1,
            side: THREE.DoubleSide,
            depthTest: false,
            depthWrite: false
        });

        const ring = new THREE.Mesh(ringGeometry, ringMaterial);
        ring.position.copy(point.position);
        ring.renderOrder = 1002;
        ring.userData.isBillboard = true;

        if (this.sahne) {
            this.sahne.add(ring);
        }

        // Store ring reference for cleanup
        circle.userData.ring = ring;
    }

    /**
     * Create label at connection point using CSS3D
     */
    private createConnectionLabel(index: number): void {
        const point = this.connectionPoints[index];
        if (!point || !this.css3dSahne) {
            return;
        }

        // Create label HTML element
        const labelEl = document.createElement('div');
        labelEl.className = 'connection-label';
        labelEl.textContent = point.label;

        Object.assign(labelEl.style, {
            fontSize: '14px',
            fontWeight: 'bold',
            fontFamily: this.style.fontAilesi,
            color: point.color || this.style.cercevRengi,
            textAlign: 'center',
            pointerEvents: 'none',
            userSelect: 'none',
            // CRITICAL for CSS3D visibility
            position: 'absolute',
            display: 'block',
            visibility: 'visible',
            opacity: '1',
            transformStyle: 'preserve-3d',
            backfaceVisibility: 'visible',
            // Make label more visible
            backgroundColor: 'rgba(255,255,255,0.9)',
            padding: '2px 6px',
            borderRadius: '3px',
            border: `1px solid ${point.color || this.style.cercevRengi}`,
            minWidth: '20px'
        });

        // Create CSS3D object for label
        const labelObj = new CSS3DObject(labelEl);
        labelObj.position.copy(point.position);

        // Scale for 3D scene - slightly larger than main annotation
        const scale = 0.015;
        labelObj.scale.set(scale, scale, scale);

        this.connectionLabels.push(labelObj);
        this.css3dSahne.add(labelObj);
    }

    /**
     * Update all leader lines when position changes
     */
    private updateLeaderLines(): void {
        this.leaderLines.forEach((line, index) => {
            const point = this.connectionPoints[index];
            if (!point) return;

            const positions = line.geometry.attributes.position as THREE.BufferAttribute;

            // Update start position (from annotation box)
            positions.setXYZ(0, this.position.x, this.position.y, this.position.z);
            // End position stays the same (at connection point)
            positions.setXYZ(1, point.position.x, point.position.y, point.position.z);

            positions.needsUpdate = true;
            line.geometry.computeBoundingSphere();
        });
    }

    /**
     * Clear all leader lines and connection points
     */
    private clearLeaderLines(): void {
        // Remove lines from scene
        this.leaderLines.forEach(line => {
            line.geometry.dispose();
            (line.material as THREE.Material).dispose();
            if (this.sahne) {
                this.sahne.remove(line);
            }
        });
        this.leaderLines = [];

        // Remove circles from scene
        this.connectionCircles.forEach(circle => {
            circle.geometry.dispose();
            (circle.material as THREE.Material).dispose();

            // Remove ring if exists
            if (circle.userData.ring) {
                const ring = circle.userData.ring as THREE.Mesh;
                ring.geometry.dispose();
                (ring.material as THREE.Material).dispose();
                if (this.sahne) {
                    this.sahne.remove(ring);
                }
            }

            if (this.sahne) {
                this.sahne.remove(circle);
            }
        });
        this.connectionCircles = [];

        // Remove labels from CSS3D scene
        this.connectionLabels.forEach(label => {
            if (this.css3dSahne) {
                this.css3dSahne.remove(label);
            }
        });
        this.connectionLabels = [];

        // Clear points array
        this.connectionPoints = [];
    }

    /**
     * Update title text
     */
    setBaslik(baslik: string): void {
        this.style.baslik = baslik;
        this.rebuildHTMLElement();
    }

    /**
     * Update description text
     */
    setAciklama(aciklama: string): void {
        this.style.aciklama = aciklama;
        this.rebuildHTMLElement();
    }

    /**
     * Update annotation style
     */
    setStyle(styleConfig: Partial<FikoAnnotationStyle>): void {
        this.style = { ...this.style, ...styleConfig };
        this.sabitBoyut = this.style.sabitBoyut;
        this.rebuildHTMLElement();

        // Update leader line colors
        this.leaderLines.forEach((line, index) => {
            const point = this.connectionPoints[index];
            const color = point?.color || this.style.cizgiRengi;
            (line.material as THREE.LineBasicMaterial).color.set(color);
        });
    }

    /**
     * Rebuild HTML element after style changes
     */
    private rebuildHTMLElement(): void {
        if (!this.htmlElement) return;

        // Clear current content
        this.htmlElement.innerHTML = '';

        // Apply updated styles - including all critical CSS3D visibility styles
        Object.assign(this.htmlElement.style, {
            backgroundColor: this.style.arkaplanRengi,
            border: `${this.style.cercevKalinlik}px solid ${this.style.cercevRengi}`,
            borderRadius: '4px',
            padding: `${this.style.padding}px`,
            maxWidth: `${this.style.maxGenislik}px`,
            minWidth: '150px',
            fontFamily: this.style.fontAilesi,
            color: this.style.metinRengi,
            boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
            // CRITICAL for CSS3D visibility
            position: 'absolute',
            display: 'block',
            visibility: 'visible',
            opacity: '1',
            zIndex: '1000',
            transformStyle: 'preserve-3d',
            backfaceVisibility: 'visible'
        });

        // Recreate title
        if (this.style.baslik) {
            const titleEl = document.createElement('div');
            titleEl.className = 'annotation-title';
            titleEl.textContent = this.style.baslik;
            Object.assign(titleEl.style, {
                fontSize: `${this.style.baslikBoyut}px`,
                fontWeight: 'bold',
                marginBottom: this.style.aciklama ? '6px' : '0',
                color: this.style.metinRengi,
                textShadow: '0 1px 2px rgba(0,0,0,0.1)'
            });
            this.htmlElement.appendChild(titleEl);
        }

        // Recreate description
        if (this.style.aciklama) {
            const descEl = document.createElement('div');
            descEl.className = 'annotation-description';
            descEl.textContent = this.style.aciklama;
            Object.assign(descEl.style, {
                fontSize: `${this.style.aciklamaBoyut}px`,
                color: this.style.metinRengi,
                opacity: '0.85'
            });
            this.htmlElement.appendChild(descEl);
        }
    }

    /**
     * Prepare annotation for scene with camera event listeners
     */
    hazirla(sahne: FikoSahne): void {
        this.sahne = sahne;

        // Get CSS3D scene from anaGosterim
        if (sahne.anaGosterim?.css3dSahne) {
            this.css3dSahne = sahne.anaGosterim.css3dSahne;
        }

        // Initialize HTML element if not already done
        if (!this.htmlElement) {
            this.init();
        }

        try {
            // Add camera change listener for fixed size
            if (!this.hazirlaListenerEklendi && sahne.anaGosterim?.aktifKontrol) {
                sahne.anaGosterim.aktifKontrol.addEventListener('change', this.kontrolDegistiEvent);
                this.hazirlaListenerEklendi = true;
            }

            // Initial scale adjustment
            this.kameraYonDegisti();
        } catch (e) {
            // Handle error silently
        }
    }

    /**
     * Handle camera zoom changes for fixed size
     */
    private _lastViewHeight = -1;

    private kameraYonDegisti(): void {
        if (!this.sabitBoyut || !this.sahne?.anaGosterim?.aktifKamera) return;

        const kamera = this.sahne.anaGosterim.aktifKamera;
        const zoom = kamera?.zoom ?? 1;

        // Calculate view height for scale adjustment
        // For orthographic camera: visible height = (top - bottom) / zoom
        const viewHeight = (kamera.top - kamera.bottom) / zoom;

        // Zoom değişmediyse (pan/rotate) scale güncellemesini atla
        if (Math.abs(viewHeight - this._lastViewHeight) < 1e-6) {
            this.updateBillboards();
            return;
        }
        this._lastViewHeight = viewHeight;

        // Scale factor for fixed size appearance
        // Target: annotation should appear ~200 pixels regardless of zoom
        // For typical CAD view, adjust based on view height
        const targetPixelSize = 200;
        const baseFactor = viewHeight / 500; // Normalize to typical view

        // Adjust CSS3D object scale
        if (this.css3dObject) {
            // Base scale is 0.02, adjust relative to view
            const scaleAdjustment = baseFactor * 0.5;
            const finalScale = Math.max(0.005, Math.min(0.1, this.originalScale.x * scaleAdjustment));
            this.css3dObject.scale.set(finalScale, finalScale, finalScale);
        }

        // Adjust connection circles and labels
        const circleScale = baseFactor * 0.5;
        this.connectionCircles.forEach(circle => {
            circle.scale.set(circleScale, circleScale, 1);

            // Scale ring too
            if (circle.userData.ring) {
                (circle.userData.ring as THREE.Mesh).scale.set(circleScale, circleScale, 1);
            }
        });

        // Adjust connection labels
        const labelScale = baseFactor * 0.3;
        this.connectionLabels.forEach(label => {
            label.scale.set(labelScale, labelScale, labelScale);
        });

        // Make circles and rings face camera (billboard)
        this.updateBillboards();
    }

    /**
     * Update billboard objects to face camera
     */
    updateBillboards(): void {
        if (!this.sahne?.anaGosterim?.aktifKamera) return;

        const camera = this.sahne.anaGosterim.aktifKamera;

        // Update circles and rings to face camera
        this.connectionCircles.forEach(circle => {
            circle.quaternion.copy(camera.quaternion);

            if (circle.userData.ring) {
                (circle.userData.ring as THREE.Mesh).quaternion.copy(camera.quaternion);
            }
        });

        // CSS3D objects automatically face camera
    }

    /**
     * Set fixed size mode
     */
    setSabitBoyut(aktif: boolean): void {
        this.sabitBoyut = aktif;
        this.style.sabitBoyut = aktif;

        if (aktif) {
            this.kameraYonDegisti();
        }
    }

    /**
     * Dispose all resources
     */
    dispose(): void {
        // Remove camera event listener
        if (this.sahne?.anaGosterim?.aktifKontrol && this.hazirlaListenerEklendi) {
            this.sahne.anaGosterim.aktifKontrol.removeEventListener('change', this.kontrolDegistiEvent);
            this.hazirlaListenerEklendi = false;
        }

        // Remove CSS3D object from scene
        if (this.css3dObject && this.css3dSahne) {
            this.css3dSahne.remove(this.css3dObject);
        }

        // Remove HTML element
        if (this.htmlElement && this.htmlElement.parentNode) {
            this.htmlElement.parentNode.removeChild(this.htmlElement);
        }

        // Clear leader lines
        this.clearLeaderLines();

        this.css3dObject = null;
        this.htmlElement = null;
        this.sahne = null;
        this.css3dSahne = null;

        this.clear();
    }

    /**
     * JSON serialization
     */
    toJSON(): any {
        return {
            type: this.type,
            uuid: this.uuid,
            name: this.name,
            style: this.style,
            position: this.position.toArray(),
            connectionPoints: this.connectionPoints.map(p => ({
                position: p.position.toArray(),
                label: p.label,
                color: p.color
            }))
        };
    }

    /**
     * JSON deserialization
     */
    static fromJSON(
        sahne: FikoSahne,
        css3dSahne: THREE.Scene,
        json: any
    ): FikoCSS3DAnnotation {
        const annotation = new FikoCSS3DAnnotation(sahne, css3dSahne, json.style);

        annotation.uuid = json.uuid;
        annotation.name = json.name;
        annotation.position.fromArray(json.position);

        // Initialize
        annotation.init();

        // Restore connection points
        if (json.connectionPoints) {
            annotation.setConnectionPoints(
                json.connectionPoints.map((p: any) => ({
                    position: new THREE.Vector3().fromArray(p.position),
                    label: p.label,
                    color: p.color
                }))
            );
        }

        return annotation;
    }
}

// Export default style for external use
export { DEFAULT_STYLE as FikoAnnotationDefaultStyle };
